import { untrack } from 'svelte'

export interface PollingOptions<Key, Value> {
  intervalMs: number
  load: (key: Key) => Promise<Value>
  onStart?: (key: Key) => void
  onSuccess: (value: Value, key: Key) => void
  onError: (error: unknown, key: Key) => void
}

export interface PollingController<Key> {
  /** Start immediately for this key, replacing and invalidating any previous run. */
  setKey: (key: Key) => void
  /** Clear the interval and ignore every response still in flight. */
  destroy: () => void
}

/**
 * Framework-free polling lifecycle used by the Svelte effect adapter below and unit tests.
 * A generation token makes both successful and rejected stale requests inert.
 */
export function createPollingController<Key, Value>(
  options: PollingOptions<Key, Value>,
): PollingController<Key> {
  let generation = 0
  let timer: ReturnType<typeof setInterval> | null = null

  const destroy = () => {
    generation++
    if (timer !== null) {
      clearInterval(timer)
      timer = null
    }
  }

  const run = async (key: Key, runGeneration: number) => {
    let value: Value
    try {
      value = await options.load(key)
    } catch (error) {
      if (runGeneration === generation) options.onError(error, key)
      return
    }
    if (runGeneration === generation) options.onSuccess(value, key)
  }

  const setKey = (key: Key) => {
    destroy()
    const runGeneration = generation
    options.onStart?.(key)
    void run(key, runGeneration)
    timer = setInterval(() => void run(key, runGeneration), options.intervalMs)
  }

  return { setKey, destroy }
}

/**
 * Poll immediately and on an interval for a reactive key.
 *
 * The key is a getter so Svelte tracks it rather than snapshotting a prop at setup time.
 * Effect cleanup invalidates in-flight work before a changed key starts or the component
 * unmounts, preventing stale callbacks from writing component state.
 */
export function usePollingResource<Key, Value>(
  getKey: () => Key,
  options: PollingOptions<Key, Value>,
): void {
  const controller = createPollingController(options)

  $effect(() => {
    const key = getKey()
    untrack(() => controller.setKey(key))
    return controller.destroy
  })
}
